import type { WidgetSize } from "schemas/generated/miniappRoutes/widgets";

import * as z from "zod";

export const widgetSizeSchema = z
  .enum(["small", "medium", "large"])
  .describe("WidgetSize");

// Root route segment for home-tab widget embeds; every mini-app mounts
// `/widget/$name` at its router root.
export const WIDGET_ROUTE_SEGMENT = "widget";

export const widgetPaths = {
  widget: ({ name }: { name: string }): string => {
    return `/${WIDGET_ROUTE_SEGMENT}/${name}`;
  },
};

// Endpoint on the mini-app origin (worker in prod, Vite middleware in dev) that
// returns data-bound json-render specs for the "spec" widgets named in its
// `widgets` query parameter. Lives beside the iframe route builder because
// it's the same cross-boundary contract: the host builds this URL, the
// mini-app serves it.
export const WIDGET_CONTENT_ROUTE_SEGMENT = "api/widget-content";

export const widgetContentPaths = {
  render: (): string => {
    return `/${WIDGET_CONTENT_ROUTE_SEGMENT}`;
  },
};

export type WidgetDefinition = {
  name: string;
  size: WidgetSize;
  description: string;
  // NOTE (Gabe, 2026-07-27): "spec" widgets are fetched from the mini-app's
  // /widget-content endpoint as a data-bound json-render spec and rendered
  // host-side with no iframe (REPL-29636). Absent means "iframe".
  renderer?: "spec" | "iframe";
};

export const WIDGET_MANIFEST: {
  miniappId: string;
  widgets: WidgetDefinition[];
}[] = [
  {
    miniappId: "brand-studio",
    widgets: [
      {
        name: "brand-card",
        size: "small",
        description: "Shows the current brand as a compact logo card.",
        renderer: "spec",
      },
      {
        name: "current-brand",
        size: "medium",
        description: "Shows brand colors, type, and logo details.",
        renderer: "spec",
      },
    ],
  },
  {
    miniappId: "assets",
    widgets: [
      {
        name: "image-mosaic",
        size: "medium",
        description: "Shows a grid of recent image assets.",
        renderer: "spec",
      },
      {
        name: "featured-image",
        size: "medium",
        description: "Shows the latest image in your asset library.",
        renderer: "spec",
      },
      {
        name: "image-strip",
        size: "large",
        description: "Shows recent images in a wide gallery strip.",
        renderer: "spec",
      },
    ],
  },
  {
    miniappId: "content",
    widgets: [
      {
        // Iframe-rendered: entries live in the site's sandbox working tree,
        // which only the app's live runtime can read (no spec endpoint).
        name: "recent-entries",
        size: "medium",
        description: "Your latest blog posts and content entries.",
      },
    ],
  },
  {
    miniappId: "files",
    widgets: [
      {
        name: "folder-card",
        size: "medium",
        description: "Shows a folder summary from project files.",
        renderer: "spec",
      },
      {
        name: "recent-file",
        size: "medium",
        description: "Shows recent files as preview cards.",
        renderer: "spec",
      },
    ],
  },
  {
    miniappId: "integrations",
    widgets: [
      {
        name: "featured-integration",
        size: "small",
        description: "Shows one recommended integration and what it unlocks.",
        renderer: "spec",
      },
      {
        name: "recommended-integrations",
        size: "medium",
        description: "Shows recommended integrations in card form.",
        renderer: "spec",
      },
    ],
  },
  {
    miniappId: "settings",
    widgets: [
      {
        name: "theme-selector",
        size: "medium",
        description: "Shows project theme controls.",
      },
      {
        name: "usage",
        size: "large",
        description: "Shows recent project usage and limits.",
        renderer: "spec",
      },
    ],
  },
  {
    miniappId: "website-builder",
    widgets: [
      {
        name: "page-improvements-large",
        size: "large",
        description: "Shows recommended improvements with a page preview.",
      },
      {
        name: "recent-pages-scroll",
        size: "large",
        description: "Shows recent pages in a horizontally scrollable strip.",
      },
      {
        name: "recommended-templates",
        size: "large",
        description:
          "Shows recommended page templates for starting a new page.",
        renderer: "spec",
      },
    ],
  },
  {
    miniappId: "skills",
    widgets: [
      {
        name: "recent-skills",
        size: "medium",
        description: "Shows recently used skills.",
        renderer: "spec",
      },
      {
        name: "recommended-skills",
        size: "large",
        description: "Shows recommended skills with more detail.",
        renderer: "spec",
      },
      {
        name: "recommended-skill",
        size: "small",
        description: "Shows 1 recommended skill.",
        renderer: "spec",
      },
    ],
  },
  {
    miniappId: "tasks",
    widgets: [
      {
        name: "upcoming-task",
        size: "small",
        description: "Shows the next upcoming task.",
        renderer: "spec",
      },
      {
        name: "monthly-calendar",
        size: "medium",
        description: "Shows tasks on a monthly calendar.",
        renderer: "spec",
      },
      {
        name: "daily-schedule",
        size: "medium",
        description: "Shows today's upcoming tasks.",
        renderer: "spec",
      },
      {
        name: "weekly-schedule",
        size: "large",
        description: "Shows tasks across the week.",
        renderer: "spec",
      },
    ],
  },
  {
    miniappId: "plans",
    widgets: [
      {
        name: "recent-plan",
        size: "medium",
        description: "Shows recent plans as preview cards.",
        renderer: "spec",
      },
      {
        name: "recent-plans",
        size: "medium",
        description: "Shows a short list of recent plans.",
        renderer: "spec",
      },
    ],
  },
];

export function findWidgetDefinition({
  miniappId,
  name,
}: {
  miniappId: string;
  name: string;
}): WidgetDefinition | null {
  const entry = WIDGET_MANIFEST.find((manifestEntry) => {
    return manifestEntry.miniappId === miniappId;
  });
  return entry?.widgets.find((widget) => widget.name === name) ?? null;
}

/**
 * Whether a basepath-relative route path is a home-tab widget embed. Anchored to
 * the first segment — NOT a substring match — so a website-builder page path that
 * merely contains the word `widget` deeper down (e.g. `/site/widget/pricing`)
 * isn't mistaken for a widget. Callers must pass a basepath-relative path (the
 * TanStack router's `location.pathname`, or the host's `miniappRoute` param), not
 * a raw `window.location.pathname` that still carries the dev `/miniapp/<id>/`
 * prefix.
 */
export function isWidgetRoute(routePath: string): boolean {
  return routePath.replace(/^\/+/u, "").split("/")[0] === WIDGET_ROUTE_SEGMENT;
}

/**
 * Most widget wheel events should scroll the home tab, but some widget content
 * can scroll internally. The mini-app shell forwards unhandled wheel deltas to
 * the host with this message, and the host scrolls its own container. It's a
 * dedicated channel rather than a typed RPC method because wheel events are
 * high-frequency and shouldn't pay per-event schema validation.
 */
export const WIDGET_WHEEL_MESSAGE_TYPE = "replo:widget-wheel";
